import {
  DateOnly,
  HistoricalSimulation,
  Holding,
  SimulationRun,
} from "@frec-js/common";
import Decimal from "decimal.js";
import { useEffect, useMemo, useState } from "react";

import {
  BetaValueInput,
  HistoricalDeltaInput,
  useBetaValuesQuery,
  useHistoricalDeltasQuery,
} from "../../generated/graphql";
import { unsafelyCastDefined } from "../../utils";

const SEED_SYMBOL = "SPY";

interface SimulationResult {
  simulationRuns: SimulationRun[];
  loading: boolean;
}

// We often use this hook in multiple components with the same holdings,
// so we cache the results to avoid re-running the simulation.
// It is easy to store too much in the cache, so we use a WeakMap.
// If we store too much, we can run into memory issues, which will crash
// the browser tab without giving us any notification.
const resultCache = new WeakMap();

/**
 * Generates data through historical backtesting.
 */
export const useHistoricalSimulation = (
  holdings: Holding[],
  skip?: boolean,
): SimulationResult => {
  const [result, setResult] = useState<SimulationRun[]>();
  const [loading, setLoading] = useState(true);
  const historyArgs: HistoricalDeltaInput = useMemo(() => {
    return {
      startDate: new DateOnly("1993-01-01"),
      symbols: [SEED_SYMBOL],
    };
  }, []);

  // serialize to improve memo perf
  const symbolsCSV = holdings
    .map((h) => h.symbol)
    .sort()
    .join(",");
  const betaArgs: BetaValueInput = useMemo(() => {
    return {
      startDate: new DateOnly("1993-01-01"),
      seedSymbol: SEED_SYMBOL,
      symbols: symbolsCSV.split(","),
    };
  }, [symbolsCSV]);

  const historyQuery = useHistoricalDeltasQuery({
    variables: { input: historyArgs },
    skip,
  });

  const betaQuery = useBetaValuesQuery({
    variables: { input: betaArgs },
    skip: skip || !holdings.length,
  });

  const dataLoading = historyQuery.loading || betaQuery.loading;

  useEffect(() => {
    if (dataLoading) return;
    const history = historyQuery.data?.historicalDeltas;
    const betas = betaQuery.data?.betaValues;
    if (
      !history ||
      !betas ||
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
      !holdings
    ) {
      setLoading(false);
      return;
    }
    if (resultCache.get(holdings)) {
      setResult(resultCache.get(holdings));
    } else {
      const firstHistoryItem = unsafelyCastDefined(history[0]);
      const seedHistory = {
        symbol: firstHistoryItem.symbol,
        deltas: firstHistoryItem.deltas.map((d) => ({
          date: d.date,
          value: new Decimal(d.close).toNumber(),
        })),
        prices: firstHistoryItem.prices.map((d) => ({
          date: d.date,
          value: new Decimal(d.close).toNumber(),
        })),
      };
      const sims = new HistoricalSimulation(
        seedHistory,
        betas,
      ).generateSimulationData(holdings);
      setResult(sims);
      resultCache.set(holdings, sims);
    }
    setLoading(false);
  }, [
    dataLoading,
    historyQuery.data?.historicalDeltas,
    betaQuery.data?.betaValues,
    holdings,
  ]);

  return {
    simulationRuns: result ?? [],
    loading: skip ? false : loading || dataLoading,
  };
};
